// ============ Organization Tab - Gestione Organizzazione ============

import { useState, useEffect, useRef, useCallback } from 'react';
import { Building2, Clock, MapPin, CopyCheck, Save, Palette, ShieldAlert, Trash2 } from 'lucide-react';
import {
  useTenantDetails,
  useUpdateTenant,
  useUpdateTenantSettings,
  useSupportedTimeZones,
} from '../../api/hooks';
import type {
  BusinessHours,
  DayHours,
  UpdateTenantData,
  UpdateTenantSettingsData,
} from '../../api/hooks';
import { DEFAULT_BUSINESS_HOURS } from '../../lib/businessHours';
import { useActiveRole, useTenantStore } from '../../store/tenantStore';
import { TimeZoneSelect } from '../ui/TimeZoneSelect';
import { useSchedulerStore } from '../../store/schedulerStore';
import { BrandingCard } from './BrandingCard';
import { DeleteOrganizationDialog } from './DeleteOrganizationDialog';
import { useReportDirty } from './settingsDirty';
import { useTranslation } from 'react-i18next';
import { useTranslatedError } from '../../hooks/useTranslatedError';
import {
  ErrorBlock,
  Feedback,
  FormField,
  LoadingBlock,
  SectionHeader,
  Select,
  SettingsCard,
  TextInput,
  Toggle,
  Button,
  type FeedbackProps,
} from './_shared';

// =============================================================================
// Constants
// =============================================================================

const DAYS_CONFIG: { key: keyof BusinessHours; labelKey: string }[] = [
  { key: 'monday', labelKey: 'days.monday' },
  { key: 'tuesday', labelKey: 'days.tuesday' },
  { key: 'wednesday', labelKey: 'days.wednesday' },
  { key: 'thursday', labelKey: 'days.thursday' },
  { key: 'friday', labelKey: 'days.friday' },
  { key: 'saturday', labelKey: 'days.saturday' },
  { key: 'sunday', labelKey: 'days.sunday' },
];

// Genera opzioni orarie da 00:00 a 23:30 a intervalli di 30min
function generateTimeOptions(): string[] {
  const opts: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      opts.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return opts;
}

const TIME_OPTIONS = generateTimeOptions();

// Sezioni della pagina. Sono tutte renderizzate una sotto l'altra: l'indice
// laterale serve a saltarci e a mostrare dove ci si trova, non a nasconderle.
type OrgSection = 'profile' | 'identity' | 'hours' | 'danger';

const SECTIONS: { id: OrgSection; labelKey: string; icon: React.ElementType }[] = [
  { id: 'profile', labelKey: 'organization.sections.profile', icon: Building2 },
  { id: 'identity', labelKey: 'organization.sections.identity', icon: Palette },
  { id: 'hours', labelKey: 'organization.sections.hours', icon: Clock },
];

/// Visibile solo all'OWNER: e' l'unico che puo' eliminare l'organizzazione.
const DANGER_SECTION = {
  id: 'danger' as const,
  labelKey: 'organization.sections.danger',
  icon: ShieldAlert,
};

/// Offset (px) dal bordo superiore dell'area scrollabile a cui una sezione è
/// considerata "quella corrente" dall'indice
const SPY_OFFSET = 96;

// =============================================================================
// Component
// =============================================================================

export function OrganizationTab() {
  const { data: tenant, isLoading, error, refetch } = useTenantDetails();
  const updateTenant = useUpdateTenant();
  const updateSettings = useUpdateTenantSettings();
  const { t } = useTranslation('settings');
  const { getErrorMessage } = useTranslatedError();
  const activeRole = useActiveRole();
  const isOwner = activeRole === 'OWNER';
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Feedback
  const [profileFeedback, setProfileFeedback] = useState<FeedbackProps | null>(null);
  const [hoursFeedback, setHoursFeedback] = useState<FeedbackProps | null>(null);

  // Profile form state
  const [profileForm, setProfileForm] = useState<UpdateTenantData>({});

  // Business hours state
  const [businessHours, setBusinessHours] = useState<BusinessHours>(DEFAULT_BUSINESS_HOURS);
  const [timezone, setTimezone] = useState<string>('');
  const { data: supportedTimeZones = [] } = useSupportedTimeZones();

  // Sezione evidenziata nell'indice + modifiche pendenti nell'identità visiva
  // (riportate dalla BrandingCard, che gestisce il proprio stato)
  const [activeSection, setActiveSection] = useState<OrgSection>('profile');
  const [brandingDirty, setBrandingDirty] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Contenitore scrollabile della pagina impostazioni (<main>): serve sia allo
  // scroll-spy sia ai salti dell'indice.
  const getScroller = useCallback(
    () => rootRef.current?.closest('main') ?? null,
    [],
  );

  // --- Scroll-spy: evidenzia nell'indice la sezione in cima alla viewport ---
  // L'IntersectionObserver fa da innesco (non serve un listener di scroll a ogni
  // pixel), il calcolo effettivo è geometrico: vince l'ultima sezione il cui
  // bordo superiore ha già superato la soglia.
  useEffect(() => {
    const scroller = getScroller();
    if (!scroller) return;

    const sections = SECTIONS.map(({ id }) => document.getElementById(`org-${id}`)).filter(
      (el): el is HTMLElement => !!el,
    );
    if (!sections.length) return;

    const update = () => {
      const scrollerTop = scroller.getBoundingClientRect().top;
      let current: OrgSection = SECTIONS[0].id;
      for (const { id } of SECTIONS) {
        const el = document.getElementById(`org-${id}`);
        if (el && el.getBoundingClientRect().top - scrollerTop <= SPY_OFFSET) {
          current = id;
        }
      }
      // In fondo alla pagina l'ultima sezione è quella che si sta guardando,
      // anche se il suo bordo superiore è più in alto della soglia.
      if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 8) {
        current = SECTIONS[SECTIONS.length - 1].id;
      }
      setActiveSection(current);
    };

    const observer = new IntersectionObserver(update, {
      root: scroller,
      // Una soglia ogni 10% di sezione: l'indice si aggiorna durante lo scorrimento
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
    });
    sections.forEach((el) => observer.observe(el));

    update();
    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      scroller.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [getScroller, tenant]);

  const scrollToSection = (id: OrgSection) => {
    const scroller = getScroller();
    const el = document.getElementById(`org-${id}`);
    if (!scroller || !el) return;
    const top =
      el.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop -
      16;
    scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    setActiveSection(id);
  };

  // Populate forms when tenant loads
  useEffect(() => {
    if (!tenant) return;
    setProfileForm({
      name: tenant.name,
      email: tenant.email || '',
      phone: tenant.phone || '',
      website: tenant.website || '',
      address: tenant.address || '',
      city: tenant.city || '',
      province: tenant.province || '',
      postalCode: tenant.postalCode || '',
    });
    setTimezone(tenant.timezone);
    if (tenant.settings?.businessHours) {
      setBusinessHours({
        ...DEFAULT_BUSINESS_HOURS,
        ...tenant.settings.businessHours,
      });
    }
  }, [tenant]);

  // --- Profile Handlers ---

  const handleProfileChange = (field: keyof UpdateTenantData, value: string) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleProfileSave = () => {
    if (!profileForm.name?.trim()) return;

    updateTenant.mutate(profileForm, {
      onSuccess: (result) => {
        // Aggiorna il nome nel tenantStore se cambiato
        const tenantStore = useTenantStore.getState();
        if (result.name !== tenantStore.activeTenantName) {
          const currentMembership = tenantStore.memberships.find(
            (m) => m.organizationId === tenantStore.activeTenantId,
          );
          if (currentMembership) {
            tenantStore.selectTenant({
              ...currentMembership,
              tenantName: result.name,
            });
            tenantStore.setMemberships(
              tenantStore.memberships.map((m) =>
                m.organizationId === tenantStore.activeTenantId
                  ? { ...m, tenantName: result.name }
                  : m,
              ),
            );
          }
        }
        setProfileFeedback({ type: 'success', message: t('organization.profileUpdated') });
        setTimeout(() => setProfileFeedback(null), 3000);
      },
      onError: (err: unknown) => {
        setProfileFeedback({
          type: 'error',
          message: getErrorMessage(err, t('organization.profileSaveError')),
        });
        setTimeout(() => setProfileFeedback(null), 5000);
      },
    });
  };

  // --- Business Hours Handlers ---

  const isDayOpen = (day: keyof BusinessHours): boolean => {
    return businessHours[day] != null;
  };

  const toggleDay = (day: keyof BusinessHours) => {
    setBusinessHours((prev) => ({
      ...prev,
      [day]: prev[day] != null ? null : { start: '08:00', end: '20:00' },
    }));
  };

  const updateDayHours = (day: keyof BusinessHours, field: keyof DayHours, value: string) => {
    setBusinessHours((prev) => ({
      ...prev,
      [day]: prev[day] ? { ...prev[day]!, [field]: value } : { start: '08:00', end: '20:00', [field]: value },
    }));
  };

  // Applica gli orari del primo giorno aperto a tutti gli altri giorni aperti
  const copyHoursToAll = () => {
    const firstOpen = DAYS_CONFIG.map((d) => businessHours[d.key]).find((h) => h != null);
    if (!firstOpen) return;
    setBusinessHours((prev) => {
      const next: BusinessHours = { ...prev };
      for (const { key } of DAYS_CONFIG) {
        if (next[key] != null) next[key] = { ...firstOpen };
      }
      return next;
    });
  };

  const handleHoursSave = () => {
    const settingsData: UpdateTenantSettingsData = {
      businessHours,
      ...(timezone && { timezone }),
    };

    updateSettings.mutate(settingsData, {
      onSuccess: (updated) => {
        // Sincronizza gli orari con lo scheduler store
        syncSchedulerHours(businessHours);
        // Il fuso governa come viene reso OGNI orario del gestionale: va
        // propagato subito, senza aspettare un nuovo login.
        useTenantStore.getState().setActiveTenantTimezone(updated.timezone);
        setHoursFeedback({ type: 'success', message: t('organization.hoursUpdated') });
        setTimeout(() => setHoursFeedback(null), 3000);
      },
      onError: (err: unknown) => {
        setHoursFeedback({
          type: 'error',
          message: getErrorMessage(err, t('organization.profileSaveError')),
        });
        setTimeout(() => setHoursFeedback(null), 5000);
      },
    });
  };

  // --- Dirty-state (calcolato prima degli early-return per non violare le
  //     regole degli hook; difensivo quando il tenant non è ancora caricato) ---
  const profileFields: (keyof UpdateTenantData)[] = [
    'name', 'email', 'phone', 'website', 'address', 'city', 'province', 'postalCode',
  ];
  const isProfileDirty =
    !!tenant && profileFields.some((f) => (profileForm[f] || '') !== ((tenant as any)[f] || ''));
  const baselineHours = tenant
    ? { ...DEFAULT_BUSINESS_HOURS, ...(tenant.settings?.businessHours || {}) }
    : DEFAULT_BUSINESS_HOURS;
  const isTimezoneDirty = !!tenant && !!timezone && timezone !== tenant.timezone;
  const isHoursDirty =
    (!!tenant && JSON.stringify(businessHours) !== JSON.stringify(baselineHours)) ||
    isTimezoneDirty;

  // Segnala alla SettingsPage le modifiche non salvate (per la guardia cambio tab)
  useReportDirty('organization', isProfileDirty || isHoursDirty);

  // --- States ---

  if (isLoading) {
    return <LoadingBlock />;
  }

  if (error || !tenant) {
    return (
      <ErrorBlock
        title={t('organization.errorTitle')}
        message={getErrorMessage(error, t('organization.loadError'))}
        retryLabel={t('common:buttons.retry')}
        onRetry={() => refetch()}
      />
    );
  }

  // Pallino di avviso sulle sezioni con modifiche non salvate
  const sections = isOwner ? [...SECTIONS, DANGER_SECTION] : SECTIONS;

  const sectionDirty: Record<OrgSection, boolean> = {
    profile: isProfileDirty,
    identity: brandingDirty,
    hours: isHoursDirty,
    // Non e' un form: non ha mai modifiche da salvare
    danger: false,
  };

  const dirtyCount = Object.values(sectionDirty).filter(Boolean).length;

  return (
    <div ref={rootRef} className="flex items-start gap-8">
      {/* Indice: fisso mentre si scorre, segue la sezione visibile */}
      <aside className="hidden lg:block w-44 flex-shrink-0 sticky top-4">
        <nav aria-label={t('organization.sections.navLabel')}>
          <ul className="space-y-0.5">
            {sections.map(({ id, labelKey, icon: Icon }) => {
              const isActive = activeSection === id;
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => scrollToSection(id)}
                    aria-current={isActive ? 'true' : undefined}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors ${
                      isActive
                        ? 'bg-white text-emerald-700 shadow-sm ring-1 ring-gray-200'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-white/70'
                    }`}
                  >
                    <Icon
                      className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-emerald-600' : 'text-gray-400'}`}
                    />
                    <span className="flex-1 truncate">{t(labelKey)}</span>
                    {sectionDirty[id] && (
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"
                        title={t('organization.unsavedChanges')}
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {dirtyCount > 0 && (
          <p className="mt-3 px-3 flex items-start gap-1.5 text-xs text-amber-600">
            <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-amber-500 flex-shrink-0" />
            {t('organization.sections.unsavedCount', { count: dirtyCount })}
          </p>
        )}
      </aside>

      <div className="flex-1 min-w-0 space-y-6">
        {/* Su schermi stretti l'indice diventa una barra di scorciatoie */}
        <nav
          className="lg:hidden sticky top-0 z-10 -mx-6 px-6 py-2.5 bg-gray-50/90 backdrop-blur border-b border-gray-200/70"
          aria-label={t('organization.sections.navLabel')}
        >
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
            {sections.map(({ id, labelKey, icon: Icon }) => {
              const isActive = activeSection === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => scrollToSection(id)}
                  aria-current={isActive ? 'true' : undefined}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-white text-emerald-700 shadow-sm ring-1 ring-gray-200'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-emerald-600' : 'text-gray-400'}`} />
                  {t(labelKey)}
                  {sectionDirty[id] && (
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  )}
                </button>
              );
            })}
          </div>
        </nav>

      {/* Sezione: Informazioni Generali + Indirizzo */}
      <SettingsCard id="org-profile">
        {/* autoComplete="off" + onSubmit preventDefault: evita il prompt
            "ricorda indirizzo/dati" di Chrome e dei password manager. */}
        <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleProfileSave(); }}>
          <SectionHeader icon={Building2} title={t('organization.generalInfo')} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={t('organization.orgName')} required className="md:col-span-2">
              <TextInput
                value={profileForm.name || ''}
                onChange={(e) => handleProfileChange('name', e.target.value)}
                placeholder={t('organization.orgNamePlaceholder')}
              />
            </FormField>

            <FormField label={t('organization.contactEmail')}>
              <TextInput
                type="email"
                value={profileForm.email || ''}
                onChange={(e) => handleProfileChange('email', e.target.value)}
                placeholder={t('organization.emailPlaceholder')}
              />
            </FormField>

            <FormField label={t('common:fields.phone')}>
              <TextInput
                type="tel"
                value={profileForm.phone || ''}
                onChange={(e) => handleProfileChange('phone', e.target.value)}
                placeholder={t('organization.phonePlaceholder')}
              />
            </FormField>

            <FormField label={t('organization.website')} className="md:col-span-2">
              <TextInput
                type="url"
                value={profileForm.website || ''}
                onChange={(e) => handleProfileChange('website', e.target.value)}
                placeholder={t('organization.websitePlaceholder')}
              />
            </FormField>
          </div>

          {/* Sottosezione Indirizzo */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-gray-400" />
              <h4 className="text-sm font-semibold text-gray-700">{t('organization.addressSection')}</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <FormField label={t('common:fields.address')} className="md:col-span-6">
                <TextInput
                  value={profileForm.address || ''}
                  onChange={(e) => handleProfileChange('address', e.target.value)}
                  placeholder={t('organization.addressPlaceholder')}
                />
              </FormField>

              <FormField label={t('common:fields.city')} className="md:col-span-3">
                <TextInput
                  value={profileForm.city || ''}
                  onChange={(e) => handleProfileChange('city', e.target.value)}
                  placeholder={t('organization.cityPlaceholder')}
                />
              </FormField>

              <FormField label={t('common:fields.province')} className="md:col-span-1">
                <TextInput
                  value={profileForm.province || ''}
                  onChange={(e) => handleProfileChange('province', e.target.value)}
                  maxLength={2}
                  placeholder={t('organization.provincePlaceholder')}
                />
              </FormField>

              <FormField label={t('common:fields.postalCode')} className="md:col-span-2">
                <TextInput
                  inputMode="numeric"
                  value={profileForm.postalCode || ''}
                  onChange={(e) => handleProfileChange('postalCode', e.target.value)}
                  placeholder={t('organization.postalCodePlaceholder')}
                />
              </FormField>
            </div>
          </div>

          {/* submit nascosto: permette il salvataggio con Invio dentro al form */}
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>

        <div className="mt-6 flex items-center gap-4">
          <Button
            onClick={handleProfileSave}
            loading={updateTenant.isPending}
            disabled={!profileForm.name?.trim() || !isProfileDirty}
            icon={Save}
          >
            {t('organization.saveProfile')}
          </Button>
          {profileFeedback ? (
            <Feedback {...profileFeedback} />
          ) : isProfileDirty ? (
            <span className="flex items-center gap-1.5 text-sm text-amber-600">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              {t('organization.unsavedChanges')}
            </span>
          ) : null}
        </div>
      </SettingsCard>

      {/* Sezione: Identità visiva (logo oppure icona + colore) */}
      <BrandingCard id="org-identity" tenant={tenant} onDirtyChange={setBrandingDirty} />

      {/* Sezione: Orari di Apertura */}
      <SettingsCard id="org-hours">
        <SectionHeader
          icon={Clock}
          title={t('organization.businessHours')}
          description={t('organization.businessHoursHint')}
          action={
            <Button variant="ghost" size="sm" icon={CopyCheck} onClick={copyHoursToAll}>
              {t('schedule.copyToAll')}
            </Button>
          }
        />

        <FormField
          label={t('organization.timezone')}
          hint={t('organization.timezoneHint')}
          className="mb-4"
        >
          <TimeZoneSelect
            // Finché l'elenco dal server non è arrivato si resta sul valore
            // corrente, così il campo non appare vuoto.
            timeZones={supportedTimeZones.length > 0 ? supportedTimeZones : timezone ? [timezone] : []}
            value={timezone}
            onChange={setTimezone}
            placeholder={t('organization.timezoneSearch')}
            noMatchLabel={t('organization.timezoneNoMatch')}
          />
        </FormField>

        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {DAYS_CONFIG.map(({ key, labelKey }) => {
            const open = isDayOpen(key);
            const hours = businessHours[key];
            return (
              <div
                key={key}
                className={`flex items-center gap-4 px-4 py-3 transition-colors ${
                  open ? 'bg-white' : 'bg-gray-50/50'
                }`}
              >
                <Toggle
                  checked={open}
                  onChange={() => toggleDay(key)}
                  aria-label={t(`common:${labelKey}`)}
                />
                <span
                  className={`w-24 text-sm font-medium ${
                    open ? 'text-gray-900' : 'text-gray-400'
                  }`}
                >
                  {t(`common:${labelKey}`)}
                </span>

                {open && hours ? (
                  <div className="flex items-center gap-2">
                    <Select
                      value={hours.start}
                      onChange={(e) => updateDayHours(key, 'start', e.target.value)}
                      className="!h-9 !py-1.5"
                    >
                      {TIME_OPTIONS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </Select>
                    <span className="text-gray-400">—</span>
                    <Select
                      value={hours.end}
                      onChange={(e) => updateDayHours(key, 'end', e.target.value)}
                      className="!h-9 !py-1.5"
                    >
                      {TIME_OPTIONS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </Select>
                  </div>
                ) : (
                  <span className="text-sm text-gray-400 italic">{t('organization.closed')}</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center gap-4">
          <Button
            onClick={handleHoursSave}
            loading={updateSettings.isPending}
            disabled={!isHoursDirty}
            icon={Save}
          >
            {t('organization.saveHours')}
          </Button>
          {hoursFeedback ? (
            <Feedback {...hoursFeedback} />
          ) : isHoursDirty ? (
            <span className="flex items-center gap-1.5 text-sm text-amber-600">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              {t('organization.unsavedChanges')}
            </span>
          ) : null}
        </div>
      </SettingsCard>

      {/* Sezione: Zona pericolosa (solo OWNER) */}
      {isOwner && (
        <SettingsCard id="org-danger" className="border-red-200">
          <SectionHeader
            icon={ShieldAlert}
            title={t('organization.danger.title')}
            description={t('organization.danger.description')}
          />

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-red-900">
                {t('organization.danger.cardTitle')}
              </p>
              <p className="text-sm text-red-700">{t('organization.danger.cardHint')}</p>
            </div>
            <Button
              variant="danger"
              icon={Trash2}
              className="flex-shrink-0"
              onClick={() => setShowDeleteDialog(true)}
            >
              {t('organization.danger.openButton')}
            </Button>
          </div>
        </SettingsCard>
      )}
      </div>

      {showDeleteDialog && (
        <DeleteOrganizationDialog
          organizationName={tenant.name}
          onClose={() => setShowDeleteDialog(false)}
        />
      )}
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Sincronizza gli orari di apertura con lo scheduler store,
 * impostando gridStartHour/gridEndHour come la fascia più ampia
 * tra tutti i giorni attivi.
 */
function syncSchedulerHours(hours: BusinessHours) {
  let minStart = 24;
  let maxEnd = 0;

  for (const day of DAYS_CONFIG) {
    const h = hours[day.key];
    if (h) {
      const startH = parseInt(h.start.split(':')[0], 10);
      const endH = parseInt(h.end.split(':')[0], 10);
      const endM = parseInt(h.end.split(':')[1], 10);
      if (startH < minStart) minStart = startH;
      const effectiveEnd = endM > 0 ? endH + 1 : endH;
      if (effectiveEnd > maxEnd) maxEnd = effectiveEnd;
    }
  }

  if (minStart >= maxEnd) {
    minStart = 8;
    maxEnd = 20;
  }

  useSchedulerStore.getState().setGridTimeRange(minStart, maxEnd);
}
